import { Event } from '../lib/types';

interface MapModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
}

export default function MapModal({ isOpen, onClose, event }: MapModalProps) {
  if (!isOpen) return null;

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.event_address || '')}`;
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(event.event_address || '')}`;
  const appleMapsUrl = (() => {
    const hasLatLng = !!(event.event_LatLong && typeof event.event_LatLong.latitude === 'number' && typeof event.event_LatLong.longitude === 'number');
    if (hasLatLng) {
      const { latitude, longitude } = event.event_LatLong!;
      // Use coordinates with optional name
      const name = event.event_location_friendly || event.event_address || '';
      return `https://maps.apple.com/?ll=${latitude},${longitude}&q=${encodeURIComponent(name)}`;
    }
    // Fallback to address query
    return `https://maps.apple.com/?q=${encodeURIComponent(event.event_address || '')}`;
  })();

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(event.event_address || '');
    } catch {
      // ignore clipboard errors
    }
  };

  const markerTarget = event.event_LatLong
    ? `${event.event_LatLong.latitude},${event.event_LatLong.longitude}`
    : (event.event_address || '');

  const centerTarget = event.event_LatLong
    ? `${event.event_LatLong.latitude},${event.event_LatLong.longitude}`
    : (event.event_address || '');

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(centerTarget)}&zoom=13&size=600x300&maptype=roadmap&markers=size:mid%7Ccolor:red%7C${encodeURIComponent(markerTarget)}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
        <div className="relative h-48">
          <img 
            src={staticMapUrl}
            alt="Map"
            className="w-full h-full object-cover"
          />
          <button 
            onClick={onClose}
            className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-lg">{event.event_location_friendly}</h3>
          <p className="text-gray-600 mt-1">{event.event_address}</p>

          <div className="mt-4 space-y-2">
            <button
              onClick={copyAddress}
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <span>Copy Address</span>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>

            {/* Apple Maps */}
            <a
              href={appleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <span>Open in Apple Maps</span>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <span>Open in Google Maps</span>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            <a
              href={wazeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <span>Open in Waze</span>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}